import { AppointmentListItem, Sorter } from '@spike/appointments-list-model';
import Spinner from 'components/UI/V3/Spinner';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { Table } from 'components/UI/V3/Table/Table';
import { FunctionComponent, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import EmptyList from '../UI/EmptyList';
import { ListType } from './AppointmentsList';
import styles from './AppointmentsListTable.module.scss';
import BoardingHeaderTable from './BoardingHeaderTable';
import BoardingTableRow from './BoardingTableRow';
import DaycareHeaderTable from './DaycareHeaderTable';
import DaycareTableRow from './DaycareTableRow';
import EvaluationHeaderTable from './EvaluationHeaderTable';
import EvaluationTableRow from './EvaluationTableRow';
import GroomingHeaderTable from './GroomingHeaderTable';
import GroomingTableRow from './GroomingTableRow';
import RequestedHeaderTable from './RequestedHeaderTable';
import RequestedTableRow from './RequestedTableRow';
import AllHeaderTable from './AllHeaderTable';
import AllTableRow from './AllTableRow';
import TransportationHeaderTable from './TransportationHeaderTable';
import TransportationTableRow from './TransportationTableRow';
import TrainingHeaderTable from './TrainingHeaderTable';
import TrainingTableRow from './TrainingTableRow';
import { WalkingHeaderTable } from './WalkingHeaderTable';
import { WalkingTableRow } from './WalkingTableRow';
import { useCardsOnFile } from './CardOnFile/useCardsOnFile';
import { useApiClientWrapper } from 'hooks';
import { fetchEvaluationStatusesThunk, fetchExpiredVaccinesThunk } from '@spike/pets-action';
import { useDispatch } from 'react-redux';

export interface Pagination {
    page: number;
    pageSize: number;
}

interface AppointmentsListTableProps {
    list: Array<AppointmentListItem>;
    listType: ListType;
    selectedTabId: string;
    isLoading?: boolean;
    sorter: Sorter | null;
    scrollerContainerId: string;
    pagination: Pagination;
    className?: string;
    onCheckout?: (bookingId: number, orderId?: number) => void;
    onShowOrder?: (bookingId: number, orderId: number) => void;
    onEditRequested?: (bookingId: number, orderId: number) => void;
    onShowClient?: (clientId: number) => void;
    onShowPet?: (petId: number) => void;
    onChangeRoomUnit?: (appointmentId: number, roomUnitId: number | null) => void;
    onRequestedStatusChanged?: () => void;
    onViewReport?: (appointmentId: number) => void;
    onAddReport?: (appointmentId: number) => void;
    onSort?: (sorter: Sorter) => void;
    onStaffChangeSaved?: () => void;
    onChangePagination?: (pagination: Pagination) => void;
    selectedIds?: Set<number>;
    onToggleSelect?: (appointmentId: number, checked: boolean) => void;
    onToggleSelectAll?: (checked: boolean, appointmentIds: Array<number>) => void;
}

export const AppointmentsListTable: FunctionComponent<AppointmentsListTableProps> = props => {
    const dispatch = useDispatch();
    const isMobile = useMediaQuery({ query: '(max-width:600px)' });
    const apiClientWrapper = useApiClientWrapper();
    const currentPageList = props.isLoading
        ? []
        : props.list.slice(
              (props.pagination.page - 1) * props.pagination.pageSize,
              props.pagination.page * props.pagination.pageSize
          );
    useCardsOnFile(apiClientWrapper, currentPageList);
    const petIds = Array.from(new Set(currentPageList.map(appointment => appointment.pet.id))).sort((a, b) => a - b);
    const petIdsKey = petIds.join(',');

    useEffect(() => {
        if (petIds.length === 0) return;

        dispatch(fetchExpiredVaccinesThunk(apiClientWrapper, petIds));
        dispatch(fetchEvaluationStatusesThunk(apiClientWrapper, petIds));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [petIdsKey]);

    const currentPageIds = currentPageList.map(app => app.appointmentId);
    const allCurrentPageSelected = currentPageIds.length > 0 && currentPageIds.every(id => props.selectedIds?.has(id));

    if (props.isLoading) {
        return (
            <div className={styles.spinnerContainer}>
                <Spinner />
            </div>
        );
    } else if (props.list.length > 0) {
        return (
            <div
                id="appointments-list-table"
                className={styles.scrollable}
            >
                <Table
                    pagination={{
                        page: props.pagination.page,
                        totalPages: Math.ceil(props.list.length / props.pagination.pageSize),
                        size: props.pagination?.pageSize,
                        isLoading: props.isLoading
                    }}
                    onPageChange={page => props.onChangePagination?.({ ...props.pagination, page })}
                    onSizeChange={pageSize => props.onChangePagination?.({ ...props.pagination, pageSize })}
                    className={props.className}
                    fullHeight={true}
                >
                    {!isMobile && props.listType === 'grooming' && (
                        <GroomingHeaderTable
                            sorter={props.sorter}
                            onSort={props.onSort}
                            allSelected={allCurrentPageSelected}
                            onToggleSelectAll={
                                props.onToggleSelectAll
                                    ? checked => props.onToggleSelectAll?.(checked, currentPageIds)
                                    : undefined
                            }
                        />
                    )}
                    {!isMobile && props.listType === 'daycare' && (
                        <DaycareHeaderTable
                            sorter={props.sorter}
                            onSort={props.onSort}
                            allSelected={allCurrentPageSelected}
                            onToggleSelectAll={
                                props.onToggleSelectAll
                                    ? checked => props.onToggleSelectAll?.(checked, currentPageIds)
                                    : undefined
                            }
                        />
                    )}
                    {!isMobile && props.listType === 'boarding' && (
                        <BoardingHeaderTable
                            sorter={props.sorter}
                            onSort={props.onSort}
                            allSelected={allCurrentPageSelected}
                            onToggleSelectAll={
                                props.onToggleSelectAll
                                    ? checked => props.onToggleSelectAll?.(checked, currentPageIds)
                                    : undefined
                            }
                        />
                    )}
                    {!isMobile && props.listType === 'evaluation' && (
                        <EvaluationHeaderTable
                            sorter={props.sorter}
                            onSort={props.onSort}
                        />
                    )}
                    {!isMobile && props.listType === 'transportation' && (
                        <TransportationHeaderTable
                            sorter={props.sorter}
                            onSort={props.onSort}
                            allSelected={allCurrentPageSelected}
                            onToggleSelectAll={
                                props.onToggleSelectAll
                                    ? checked => props.onToggleSelectAll?.(checked, currentPageIds)
                                    : undefined
                            }
                        />
                    )}
                    {!isMobile && props.listType === 'training' && (
                        <TrainingHeaderTable
                            sorter={props.sorter}
                            onSort={props.onSort}
                            allSelected={allCurrentPageSelected}
                            onToggleSelectAll={
                                props.onToggleSelectAll
                                    ? checked => props.onToggleSelectAll?.(checked, currentPageIds)
                                    : undefined
                            }
                        />
                    )}
                    {!isMobile && props.listType === 'walking' && (
                        <WalkingHeaderTable
                            sorter={props.sorter}
                            onSort={props.onSort}
                            allSelected={allCurrentPageSelected}
                            onToggleSelectAll={
                                props.onToggleSelectAll
                                    ? checked => props.onToggleSelectAll?.(checked, currentPageIds)
                                    : undefined
                            }
                        />
                    )}
                    {!isMobile && props.listType === 'requested' && (
                        <RequestedHeaderTable
                            selectedTabId={props.selectedTabId}
                            sorter={props.sorter}
                            onSort={props.onSort}
                        />
                    )}
                    {!isMobile && ['all', 'client'].includes(props.listType) && (
                        <AllHeaderTable
                            hideClientHeader={props.listType === 'client'}
                            sorter={props.sorter}
                            onSort={props.onSort}
                        />
                    )}
                    <TableBody>
                        {currentPageList.map((app, index) => {
                            switch (props.listType) {
                                case 'grooming':
                                    return (
                                        <GroomingTableRow
                                            key={app.appointmentId}
                                            app={app}
                                            isLast={props.list.length === index + 1}
                                            scrollerContainerId={props.scrollerContainerId}
                                            onCheckout={() => props.onCheckout?.(app.bookingId, app.orderId)}
                                            onShowOrder={() => props.onShowOrder?.(app.bookingId, app.orderId)}
                                            onShowClient={() => props.onShowClient?.(app.customer.id)}
                                            onShowPet={() => props.onShowPet?.(app.pet.id)}
                                            onViewReport={() => props.onViewReport?.(app.appointmentId)}
                                            onAddReport={() => props.onAddReport?.(app.appointmentId)}
                                            onStaffSaved={props.onStaffChangeSaved}
                                            selected={props.selectedIds?.has(app.appointmentId)}
                                            onToggleSelect={
                                                props.onToggleSelect
                                                    ? checked => props.onToggleSelect?.(app.appointmentId, checked)
                                                    : undefined
                                            }
                                        />
                                    );
                                case 'daycare':
                                    return (
                                        <DaycareTableRow
                                            key={app.appointmentId}
                                            app={app}
                                            isLast={props.list.length === index + 1}
                                            scrollerContainerId={props.scrollerContainerId}
                                            onCheckout={() => props.onCheckout?.(app.bookingId, app.orderId)}
                                            onShowOrder={() => props.onShowOrder?.(app.bookingId, app.orderId)}
                                            onShowClient={() => props.onShowClient?.(app.customer.id)}
                                            onShowPet={() => props.onShowPet?.(app.pet.id)}
                                            onChangeRoomUnit={roomUnitId =>
                                                props.onChangeRoomUnit?.(app.appointmentId, roomUnitId)
                                            }
                                            selected={props.selectedIds?.has(app.appointmentId)}
                                            onToggleSelect={
                                                props.onToggleSelect
                                                    ? checked => props.onToggleSelect?.(app.appointmentId, checked)
                                                    : undefined
                                            }
                                        />
                                    );
                                case 'boarding':
                                    return (
                                        <BoardingTableRow
                                            key={app.appointmentId}
                                            app={app}
                                            isLast={props.list.length === index + 1}
                                            scrollerContainerId={props.scrollerContainerId}
                                            onCheckout={() => props.onCheckout?.(app.bookingId, app.orderId)}
                                            onShowOrder={() => props.onShowOrder?.(app.bookingId, app.orderId)}
                                            onShowClient={() => props.onShowClient?.(app.customer.id)}
                                            onShowPet={() => props.onShowPet?.(app.pet.id)}
                                            onChangeRoomUnit={roomUnitId =>
                                                props.onChangeRoomUnit?.(app.appointmentId, roomUnitId)
                                            }
                                            selected={props.selectedIds?.has(app.appointmentId)}
                                            onToggleSelect={
                                                props.onToggleSelect
                                                    ? checked => props.onToggleSelect?.(app.appointmentId, checked)
                                                    : undefined
                                            }
                                        />
                                    );
                                case 'evaluation':
                                    return (
                                        <EvaluationTableRow
                                            key={app.appointmentId}
                                            app={app}
                                            isLast={props.list.length === index + 1}
                                            scrollerContainerId={props.scrollerContainerId}
                                            onCheckout={() => props.onCheckout?.(app.bookingId, app.orderId)}
                                            onShowOrder={() => props.onShowOrder?.(app.bookingId, app.orderId)}
                                            onShowClient={() => props.onShowClient?.(app.customer.id)}
                                            onShowPet={() => props.onShowPet?.(app.pet.id)}
                                        />
                                    );
                                case 'transportation':
                                    return (
                                        <TransportationTableRow
                                            key={app.appointmentId}
                                            app={app}
                                            isLast={props.list.length === index + 1}
                                            scrollerContainerId={props.scrollerContainerId}
                                            onCheckout={() => props.onCheckout?.(app.bookingId, app.orderId)}
                                            onShowOrder={() => props.onShowOrder?.(app.bookingId, app.orderId)}
                                            onShowClient={() => props.onShowClient?.(app.customer.id)}
                                            onShowPet={() => props.onShowPet?.(app.pet.id)}
                                            onStaffSaved={props.onStaffChangeSaved}
                                            selected={props.selectedIds?.has(app.appointmentId)}
                                            onToggleSelect={
                                                props.onToggleSelect
                                                    ? checked => props.onToggleSelect?.(app.appointmentId, checked)
                                                    : undefined
                                            }
                                        />
                                    );
                                case 'training':
                                    return (
                                        <TrainingTableRow
                                            key={app.appointmentId}
                                            app={app}
                                            isLast={props.list.length === index + 1}
                                            scrollerContainerId={props.scrollerContainerId}
                                            onCheckout={() => props.onCheckout?.(app.bookingId, app.orderId)}
                                            onShowOrder={() => props.onShowOrder?.(app.bookingId, app.orderId)}
                                            onShowClient={() => props.onShowClient?.(app.customer.id)}
                                            onShowPet={() => props.onShowPet?.(app.pet.id)}
                                            onStaffSaved={props.onStaffChangeSaved}
                                            selected={props.selectedIds?.has(app.appointmentId)}
                                            onToggleSelect={
                                                props.onToggleSelect
                                                    ? checked => props.onToggleSelect?.(app.appointmentId, checked)
                                                    : undefined
                                            }
                                        />
                                    );
                                case 'walking':
                                    return (
                                        <WalkingTableRow
                                            key={app.appointmentId}
                                            app={app}
                                            isLast={props.list.length === index + 1}
                                            scrollerContainerId={props.scrollerContainerId}
                                            onCheckout={() => props.onCheckout?.(app.bookingId, app.orderId)}
                                            onShowOrder={() => props.onShowOrder?.(app.bookingId, app.orderId)}
                                            onShowClient={() => props.onShowClient?.(app.customer.id)}
                                            onShowPet={() => props.onShowPet?.(app.pet.id)}
                                            onStaffSaved={props.onStaffChangeSaved}
                                            selected={props.selectedIds?.has(app.appointmentId)}
                                            onToggleSelect={
                                                props.onToggleSelect
                                                    ? checked => props.onToggleSelect?.(app.appointmentId, checked)
                                                    : undefined
                                            }
                                        />
                                    );
                                case 'requested':
                                    return (
                                        <RequestedTableRow
                                            key={app.appointmentId}
                                            app={app}
                                            selectedTabId={props.selectedTabId}
                                            isLast={props.list.length === index + 1}
                                            scrollerContainerId={props.scrollerContainerId}
                                            onCheckout={() => props.onCheckout?.(app.bookingId, app.orderId)}
                                            onShowOrder={() => props.onShowOrder?.(app.bookingId, app.orderId)}
                                            onEdit={() => props.onEditRequested?.(app.bookingId, app.orderId)}
                                            onShowClient={() => props.onShowClient?.(app.customer.id)}
                                            onShowPet={() => props.onShowPet?.(app.pet.id)}
                                            onStatusChanged={props.onRequestedStatusChanged}
                                        />
                                    );
                                case 'all':
                                case 'client':
                                    return (
                                        <AllTableRow
                                            key={app.appointmentId}
                                            app={app}
                                            hideClientColumn={props.listType === 'client'}
                                            isLast={props.list.length === index + 1}
                                            scrollerContainerId={props.scrollerContainerId}
                                            onCheckout={() => props.onCheckout?.(app.bookingId, app.orderId)}
                                            onShowOrder={() => props.onShowOrder?.(app.bookingId, app.orderId)}
                                            onShowClient={() => props.onShowClient?.(app.customer.id)}
                                            onShowPet={() => props.onShowPet?.(app.pet.id)}
                                            onViewReport={() => props.onViewReport?.(app.appointmentId)}
                                            onAddReport={() => props.onAddReport?.(app.appointmentId)}
                                        />
                                    );
                                default:
                                    return null;
                            }
                        })}
                    </TableBody>
                </Table>
            </div>
        );
    } else {
        return <EmptyList variant="upcoming" />;
    }
};

export default AppointmentsListTable;
